import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSupplier,
  deleteSupplier,
  listSuppliers,
  updateSupplier,
  type Supplier,
  type SupplierInput,
} from '@/lib/api/suppliers.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const emptyForm = (): SupplierInput => ({
  name: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  website: '',
  address: '',
  accountNumber: '',
  paymentTerms: '',
  defaultLeadTimeDays: null,
  notes: '',
  active: true,
});

export function SuppliersSettingsPage() {
  const queryClient = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<SupplierInput>(emptyForm());

  const suppliersQuery = useQuery({
    queryKey: ['suppliers', showInactive ? 'all' : 'active'],
    queryFn: () => listSuppliers({ active: showInactive ? 'all' : true }),
  });

  function openCreate() {
    setEditing(null);
    setCreating(true);
    setForm(emptyForm());
  }

  function openEdit(s: Supplier) {
    setCreating(false);
    setEditing(s);
    setForm({
      name: s.name,
      contactName: s.contactName ?? '',
      contactEmail: s.contactEmail ?? '',
      contactPhone: s.contactPhone ?? '',
      website: s.website ?? '',
      address: s.address ?? '',
      accountNumber: s.accountNumber ?? '',
      paymentTerms: s.paymentTerms ?? '',
      defaultLeadTimeDays: s.defaultLeadTimeDays ?? null,
      notes: s.notes ?? '',
      active: s.active !== false,
    });
  }

  function closeForm() {
    setEditing(null);
    setCreating(false);
    setForm(emptyForm());
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: SupplierInput = {
        name: form.name.trim(),
        contactName: form.contactName?.toString().trim() || null,
        contactEmail: form.contactEmail?.toString().trim() || null,
        contactPhone: form.contactPhone?.toString().trim() || null,
        website: form.website?.toString().trim() || null,
        address: form.address?.toString().trim() || null,
        accountNumber: form.accountNumber?.toString().trim() || null,
        paymentTerms: form.paymentTerms?.toString().trim() || null,
        defaultLeadTimeDays:
          form.defaultLeadTimeDays != null && form.defaultLeadTimeDays !== ('' as never)
            ? Number(form.defaultLeadTimeDays)
            : null,
        notes: form.notes?.toString().trim() || null,
        active: form.active !== false,
      };
      if (editing) return updateSupplier(editing.id, body);
      return createSupplier(body);
    },
    onSuccess: () => {
      toastSuccess(editing ? 'Supplier updated' : 'Supplier created');
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      void queryClient.invalidateQueries({ queryKey: ['suppliers-active'] });
      closeForm();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => deleteSupplier(id),
    onSuccess: () => {
      toastSuccess('Supplier archived');
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => deleteSupplier(id, { hard: true }),
    onSuccess: () => {
      toastSuccess('Supplier deleted');
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => updateSupplier(id, { active: true }),
    onSuccess: () => {
      toastSuccess('Supplier restored');
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    saveMutation.mutate();
  }

  const rows = (suppliersQuery.data ?? []).filter((s) => showInactive || s.active !== false);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-950">Suppliers</h2>
          <p className="text-sm text-slate-500">
            Vendors for purchase orders, RMAs, and inventory supplier SKUs.
          </p>
        </div>
        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
          onClick={openCreate}
        >
          + New Supplier
        </button>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
        Show archived
      </label>

      {creating || editing ? (
        <form
          className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          onSubmit={handleSubmit}
        >
          <h3 className="font-semibold text-slate-950">
            {editing ? `Edit ${editing.name}` : 'New supplier'}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ['name', 'Name *'],
                ['contactName', 'Contact name'],
                ['contactEmail', 'Email'],
                ['contactPhone', 'Phone'],
                ['website', 'Website'],
                ['accountNumber', 'Account number'],
                ['paymentTerms', 'Payment terms'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="block text-sm">
                <span className="font-medium text-slate-700">{label}</span>
                <input
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  value={String(form[key] ?? '')}
                  onChange={(e) => setForm((c) => ({ ...c, [key]: e.target.value }))}
                  required={key === 'name'}
                />
              </label>
            ))}
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Default lead time (days)</span>
              <input
                type="number"
                min={0}
                className="mt-1 w-full rounded-xl border px-3 py-2"
                value={form.defaultLeadTimeDays ?? ''}
                onChange={(e) =>
                  setForm((c) => ({
                    ...c,
                    defaultLeadTimeDays: e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Address</span>
            <textarea
              className="mt-1 w-full rounded-xl border px-3 py-2"
              rows={2}
              value={String(form.address ?? '')}
              onChange={(e) => setForm((c) => ({ ...c, address: e.target.value }))}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Notes</span>
            <textarea
              className="mt-1 w-full rounded-xl border px-3 py-2"
              rows={2}
              value={String(form.notes ?? '')}
              onChange={(e) => setForm((c) => ({ ...c, notes: e.target.value }))}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active !== false}
              onChange={(e) => setForm((c) => ({ ...c, active: e.target.checked }))}
            />
            Active
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="rounded-xl border px-4 py-2 text-sm font-semibold"
              onClick={closeForm}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Items</th>
              <th className="px-4 py-3">Open POs</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((s) => (
              <tr key={s.id} className={s.active === false ? 'opacity-60' : ''}>
                <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                <td className="px-4 py-3 text-slate-600">
                  {[s.contactName, s.contactEmail, s.contactPhone].filter(Boolean).join(' · ') ||
                    '—'}
                </td>
                <td className="px-4 py-3">{s.itemCount ?? 0}</td>
                <td className="px-4 py-3">{s.openPoCount ?? 0}</td>
                <td className="px-4 py-3">{s.active === false ? 'Archived' : 'Active'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="text-xs font-semibold text-primary"
                      onClick={() => openEdit(s)}
                    >
                      Edit
                    </button>
                    {s.active === false ? (
                      <button
                        type="button"
                        className="text-xs font-semibold text-slate-600"
                        onClick={() => restoreMutation.mutate(s.id)}
                      >
                        Restore
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="text-xs font-semibold text-amber-700"
                        onClick={() => {
                          if (window.confirm(`Archive “${s.name}”?`)) archiveMutation.mutate(s.id);
                        }}
                      >
                        Archive
                      </button>
                    )}
                    {(s.itemCount ?? 0) + (s.openPoCount ?? 0) === 0 && s.active === false ? (
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-600"
                        onClick={() => {
                          if (window.confirm(`Permanently delete “${s.name}”?`))
                            hardDeleteMutation.mutate(s.id);
                        }}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">No suppliers yet.</p>
        ) : null}
      </div>
    </div>
  );
}
